import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { useAuth } from "../auth/AuthProvider";
import { Icon, ProviderMark } from "../components/icons";
import { Modal } from "../components/Modal";
import { Alert, Button, Card, CardHeader, LoadingBlock, PageHeader } from "../components/ui";
import { ApiError } from "../lib/api";
import { relativeTime } from "../lib/format";
import {
  useDisconnectGit,
  useGitConnections,
  useGitProviders,
  useStartGitAuth,
} from "../lib/queries";
import type { GitConnection, GitProviderId, GitProviderInfo } from "../lib/types";
import { useSigninHostCandidate } from "../components/ConnectSigninHost";

/**
 * Git hosts, one row each.
 *
 * The page used to be two sections that described each other: a list of
 * connected accounts, and below it a grid of provider tiles headed "Connect a
 * git host" whose paragraph explained deploy keys and webhooks — the same two
 * facts the security card at the bottom then listed again. A host that was
 * connected appeared in both, described differently in each.
 *
 * So it is one list keyed by the host rather than by the connection. There are
 * exactly two: `providers/git/index.ts` maps `github` and `gitlab` and nothing
 * else, and `bitbucket` exists only in the database enum so that adding it
 * later needs no migration. The old grid was `sm:grid-cols-3` and left a dead
 * column for a host the API has never offered.
 *
 * Each row carries its own state — connect it, the accounts already on it, or
 * why it cannot be used here — so there is one place to look per host and
 * nothing that has to be said twice.
 */
export function IntegrationsPage() {
  const { t } = useTranslation("settings");
  const { currentOrg } = useAuth();
  const orgId = currentOrg?.id;
  const canManage = currentOrg?.role === "owner" || currentOrg?.role === "admin";

  const providers = useGitProviders(orgId);
  const connections = useGitConnections(orgId);
  const startAuth = useStartGitAuth(orgId ?? "");
  const disconnect = useDisconnectGit(orgId ?? "");

  const signinHost = useSigninHostCandidate(orgId);
  const [searchParams, setSearchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<GitConnection | null>(null);

  // The OAuth callback lands back here with its outcome in the query string.
  // Read it once, then strip it so a refresh does not replay the banner.
  const connected = searchParams.get("git_connected");
  const callbackError = searchParams.get("git_error");

  useEffect(() => {
    if (!connected && !callbackError) return;
    if (callbackError) setError(callbackError);
    const next = new URLSearchParams(searchParams);
    next.delete("git_connected");
    next.delete("git_error");
    setSearchParams(next, { replace: true });
  }, [connected, callbackError, searchParams, setSearchParams]);

  async function connect(provider: GitProviderId) {
    setError(null);
    try {
      const { authorizeUrl } = await startAuth.mutateAsync({
        provider,
        redirectTo: "/settings/integrations",
      });
      // A full navigation, not a fetch: the consent screen is the git host's
      // own page and has to be rendered by the browser.
      window.location.assign(authorizeUrl);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t("integrations.startFailed"));
    }
  }

  async function confirmDisconnect() {
    if (!pending) return;
    setError(null);
    try {
      await disconnect.mutateAsync(pending.id);
      setPending(null);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t("integrations.disconnectFailed"));
      setPending(null);
    }
  }

  const linked = connections.data ?? [];
  const available = providers.data ?? [];
  const usable = available.filter((provider) => provider.configured);

  return (
    <div className="space-y-6">
      {/*
        The organization is named because this page is reached from the user
        menu, where everything else belongs to the person signed in. A
        connection belongs to the organization its apps deploy from, so somebody
        in more than one needs to know which set they are about to disconnect.
      */}
      <PageHeader
        title={t("integrations.title")}
        description={
          currentOrg
            ? t("integrations.description", { organization: currentOrg.name })
            : t("integrations.descriptionNoOrg")
        }
      />

      {connected && !callbackError && (
        <Alert tone="success">
          {t("integrations.connected", {
            provider: available.find((p) => p.id === connected)?.label ?? connected,
          })}
        </Alert>
      )}
      {error && <Alert>{error}</Alert>}

      {connections.isLoading || providers.isLoading ? (
        <LoadingBlock label={t("integrations.loading")} />
      ) : (
        <>
          <Card>
            <CardHeader title={t("integrations.hosts")} />
            <ul className="divide-y divide-line">
              {available.map((provider) => (
                <ProviderRow
                  key={provider.id}
                  provider={provider}
                  accounts={linked.filter((c) => c.provider === provider.id)}
                  canManage={canManage}
                  busy={startAuth.isPending}
                  /* The sign-in identity belongs in the host's own row -- a
                     banner above it repeated the row's button. */
                  signedInAs={signinHost?.provider === provider.id ? signinHost.login : null}
                  onConnect={() => void connect(provider.id)}
                  onDisconnect={setPending}
                />
              ))}
            </ul>

            {/*
              One line at the foot for whichever of three things is true:
              the role cannot connect; nothing is configured; or -- the usual
              case -- what a connection is allowed to do, in the two facts
              somebody actually wants (the key cannot push; the token never
              reaches a build server). The four-bullet card this replaces was
              a paragraph on a page about state.
            */}
            <p className="flex items-start gap-2 border-t border-line px-5 py-3 text-xs leading-5 text-fg-subtle">
              <Icon name="lock" className="mt-0.5 size-3.5 shrink-0" />
              <span>
                {!canManage
                  ? t("integrations.needsAdmin")
                  : usable.length === 0
                    ? t("integrations.noneUsable")
                    : t("integrations.access")}
              </span>
            </p>
          </Card>
        </>
      )}

      <Modal
        open={pending !== null}
        onClose={() => setPending(null)}
        title={t("integrations.disconnect.title", { account: pending?.account.login ?? "" })}
      >
        <div className="space-y-4">
          {/*
            The count is named when there is one. "Apps that use it keep
            working" is the reassuring half and it is true; the half somebody
            needs before clicking is that the keys and webhooks stay behind on
            their repositories, which only deleting the app removes.
          */}
          <p className="text-sm leading-6 text-fg-muted">
            {pending?.appCount
              ? t("integrations.disconnect.inUse", { count: pending.appCount })
              : ""}
            {t("integrations.disconnect.body")}
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setPending(null)}>
              {t("integrations.disconnect.cancel")}
            </Button>
            <Button
              variant="danger"
              loading={disconnect.isPending}
              onClick={() => void confirmDisconnect()}
            >
              {t("integrations.disconnect.action")}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

/**
 * One host, with whatever is true of it underneath.
 *
 * The action sits on the header line and says which of the two things it does:
 * a host with no account on it is "Connect", one that already has an account is
 * "Add account", because on both hosts a second authorization is a second
 * account rather than a repeat of the first.
 */
function ProviderRow({
  provider,
  accounts,
  canManage,
  busy,
  signedInAs,
  onConnect,
  onDisconnect,
}: {
  provider: GitProviderInfo;
  accounts: GitConnection[];
  canManage: boolean;
  busy: boolean;
  /** The login this person signed in to Maxlayer with on this host, when not yet connected. */
  signedInAs: string | null;
  onConnect: () => void;
  onDisconnect: (connection: GitConnection) => void;
}) {
  const { t } = useTranslation("settings");
  const status = !provider.configured
    ? t("integrations.notAvailable")
    : accounts.length > 0
      ? t("integrations.accountCount", { count: accounts.length })
      : signedInAs
        ? t("integrations.signedInAs", { login: signedInAs })
        : t("integrations.notConnected");

  return (
    <li className="px-5 py-4">
      {/* Mark in the host's colour, name and a status line, action on the
          right: the row says what state it is in before the button is read. */}
      <div className="grid grid-cols-[auto_1fr_auto] items-center gap-x-3">
        <ProviderMark
          provider={provider.id}
          className={`size-6 ${provider.id === "gitlab" ? "text-[#fc6d26]" : "text-fg"} ${provider.configured ? "" : "opacity-40"}`}
        />
        <div className="min-w-0">
          <p className="text-sm font-medium text-fg">{provider.label}</p>
          {/* Wraps on a phone, where a truncated status line loses its sentence. */}
          <p className="text-xs leading-5 text-fg-subtle sm:truncate">{status}</p>
        </div>
        {provider.configured && canManage && (
          <Button
            size="sm"
            variant={accounts.length > 0 ? "secondary" : "primary"}
            disabled={busy}
            onClick={onConnect}
          >
            {accounts.length > 0 ? t("integrations.addAccount") : t("integrations.connect")}
          </Button>
        )}
      </div>

      {accounts.length > 0 && (
        <ul className="mt-3 divide-y divide-line rounded-md border border-line">
          {accounts.map((account) => (
            <li key={account.id} className="grid grid-cols-[auto_1fr_auto] items-center gap-x-3 px-3.5 py-2.5">
              <AccountAvatar login={account.account.login} url={account.account.avatarUrl} />
              <div className="min-w-0">
                <p className="truncate text-[13px] text-fg">
                  {account.account.login}
                  {account.account.type === "organization" && (
                    <span className="ml-2 rounded bg-raised px-1.5 py-0.5 text-[11px] text-fg-subtle">
                      {t("integrations.organization")}
                    </span>
                  )}
                </p>
                <p className="truncate text-xs text-fg-subtle">
                  {account.appCount !== null && account.appCount > 0
                    ? `${t("integrations.appCount", { count: account.appCount })} · `
                    : ""}
                  {relativeTime(account.connectedAt)}
                </p>
              </div>
              {canManage && (
                <Button size="sm" variant="ghost" onClick={() => onDisconnect(account)}>
                  {t("integrations.disconnect.action")}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

/**
 * The host's avatar, or the login's initials. Initials also take over when
 * the image does not load -- a dead URL, a host that changed its CDN, a
 * policy that blocks it -- because a broken-image glyph beside a name reads
 * as a fault in the row rather than a missing picture.
 */
function AccountAvatar({ login, url }: { login: string; url: string | null }) {
  const [failed, setFailed] = useState(false);
  if (url && !failed) {
    return (
      <img
        src={url}
        alt=""
        width={24}
        height={24}
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
        className="size-6 rounded-full bg-raised"
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      className="grid size-6 place-items-center rounded-full bg-raised text-[11px] font-medium uppercase text-fg-muted"
    >
      {login.slice(0, 2)}
    </span>
  );
}
